import React, { useState } from 'react';
import SEO from '../components/SEO';
import './Support.css';

const faqs = [
  {
    q: 'How do I connect my phone to my TV?',
    a: 'Make sure your phone and TV are connected to the same WiFi network. Open the app, tap "Discover TV" and the app will automatically find your TV. Select your TV from the list to start controlling it.',
  },
  {
    q: 'Which TV brands are supported?',
    a: 'We support 70+ TV brands including Samsung, LG, Sony, TCL, Philips, Panasonic, Sharp, Toshiba, Hisense, Vizio, Haier, Xiaomi, and many more. Both WiFi-connected smart TVs and IR blaster-controlled TVs are supported.',
  },
  {
    q: 'Does the app work without WiFi?',
    a: 'Yes! If your phone has an IR blaster, you can control any TV using infrared signals without WiFi. Simply select your TV brand and the app will send IR commands directly to your TV.',
  },
  {
    q: 'How does AC remote control work?',
    a: 'The AC remote uses your phone\'s IR blaster to send commands to your air conditioner. Select your AC brand, and you can control temperature, fan speed, modes, swing, and more.',
  },
  {
    q: 'What is Screen Mirroring?',
    a: 'Screen Mirroring allows you to display your phone screen on your TV. You can share photos, videos, and other media wirelessly. Both your phone and TV need to be on the same WiFi network.',
  },
  {
    q: 'How do I use IPTV features?',
    a: 'Go to the IPTV section in the app. You can browse channels by country from our built-in playlist or import your own custom M3U playlist files.',
  },
  {
    q: 'The remote is not working. What should I do?',
    a: 'First, ensure your phone and TV are on the same WiFi network. Try restarting both devices. If using IR, make sure your phone has an IR blaster and point it directly at the TV. You can also try re-discovering your TV.',
  },
  {
    q: 'Is the app free to use?',
    a: 'Yes, the app is free to download and use. Some features may include ads to support ongoing development.',
  },
];

const Support = () => {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <div className="support-page">
      <SEO
        title="Support & FAQ – Remotix: Universal TV Remote"
        description="Get help with Remotix: Universal TV Remote. Find answers to common questions about TV connection, IR blaster support, AC control, screen mirroring, and more."
      />
      <section className="page-hero">
        <div className="page-hero-bg">
          <div className="hero-glow hero-glow-1"></div>
        </div>
        <div className="page-hero-content">
          <span className="section-badge">Help Center</span>
          <h1>Customer <span className="gradient-text">Support</span></h1>
          <p>Find answers to common questions or reach out to our support team.</p>
        </div>
      </section>

      <section className="support-content">
        <div className="support-container">
          {/* Contact Cards */}
          <div className="contact-grid">
            <div className="contact-card">
              <div className="contact-icon">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                  <path d="M6 10l10 7 10-7" stroke="#1976D2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <rect x="4" y="8" width="24" height="16" rx="2" stroke="#1976D2" strokeWidth="2" fill="none" />
                </svg>
              </div>
              <h3>Email Support</h3>
              <p>Get a response within 24 hours</p>
              <a href="mailto:support@universaltvremote.app" className="contact-link">
                support@universaltvremote.app
              </a>
            </div>

            <div className="contact-card">
              <div className="contact-icon">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                  <circle cx="16" cy="16" r="12" stroke="#7C4DFF" strokeWidth="2" fill="none" />
                  <path d="M16 10v6l4 2" stroke="#7C4DFF" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </div>
              <h3>Response Time</h3>
              <p>We typically respond within</p>
              <span className="contact-link">24 - 48 hours</span>
            </div>

            <div className="contact-card">
              <div className="contact-icon">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                  <path d="M8 12a8 8 0 1116 0v4a4 4 0 01-4 4h-2" stroke="#43A047" strokeWidth="2" strokeLinecap="round" fill="none" />
                  <circle cx="16" cy="24" r="2" fill="#43A047" />
                </svg>
              </div>
              <h3>In-App Help</h3>
              <p>Access guides and tutorials</p>
              <span className="contact-link">Settings &rarr; How to Use</span>
            </div>
          </div>

          {/* FAQs */}
          <div className="faq-section">
            <h2>Frequently Asked <span className="gradient-text">Questions</span></h2>
            <div className="faq-list">
              {faqs.map((faq, i) => (
                <div
                  className={`faq-item ${openIndex === i ? 'open' : ''}`}
                  key={i}
                  onClick={() => setOpenIndex(openIndex === i ? null : i)}
                >
                  <div className="faq-question">
                    <span>{faq.q}</span>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      className="faq-chevron"
                    >
                      <path
                        d="M5 8l5 5 5-5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div className="faq-answer">
                    <p>{faq.a}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Contact Form */}
          <div className="contact-form-section">
            <h2>Still Need <span className="gradient-text">Help?</span></h2>
            <p className="form-desc">Send us a message and we'll get back to you as soon as possible.</p>
            <form className="contact-form" onSubmit={(e) => { e.preventDefault(); alert('Thank you! We will get back to you soon.'); }}>
              <div className="form-row">
                <div className="form-group">
                  <label>Name</label>
                  <input type="text" placeholder="Your name" required />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" placeholder="your@email.com" required />
                </div>
              </div>
              <div className="form-group">
                <label>Subject</label>
                <select required defaultValue="">
                  <option value="" disabled>Select a topic</option>
                  <option>TV Connection Issue</option>
                  <option>IR Blaster Not Working</option>
                  <option>Screen Mirroring Help</option>
                  <option>IPTV Issue</option>
                  <option>Feature Request</option>
                  <option>Other</option>
                </select>
              </div>
              <div className="form-group">
                <label>Message</label>
                <textarea rows="5" placeholder="Describe your issue..." required></textarea>
              </div>
              <button type="submit" className="btn-primary">Send Message</button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Support;
